// hooks/useStockDetails.ts
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { StockStore, ItemStop } from "../types/types";
import { AvailabilityStatus } from "../types/enums";
import { selectStoreList } from "../../store/slices/storeListSlice";

interface UseStockDetailsProps {
  stockStores?: StockStore[];
  stockEnabled: boolean;
  forceStore?: boolean;
  itemStops?: ItemStop[];
  selectedStoreIds: number[];
  availabilityStatus?: AvailabilityStatus;
}

interface AvailableStore {
  name: string;
  available: number;
  total: number;
}

interface AvailabilityByStore {
  name: string;
  available: boolean;
}

export const useStockDetails = ({
  stockStores,
  stockEnabled,
  forceStore = false,
  itemStops = [],
  selectedStoreIds,
  availabilityStatus,
}: UseStockDetailsProps): {
  availableStores: AvailableStore[];
  availabilityByStores: AvailabilityByStore[];
  totalAvailable: number;
  showNoStoreMessage: boolean;
  showUnavailableMessage: boolean;
} => {
  const storeList = useSelector(selectStoreList);
  const storeMap = useMemo(() => {
    const map = new Map<number, string>();
    storeList.forEach((store) => map.set(store.id, store.name));
    return map;
  }, [storeList]);

  // Остатки — данные из stockStores (связь товара)
  const availableStores = useMemo(() => {
    if (!stockEnabled || !stockStores?.length) return [];

    // Если принудительный выбор и точка выбрана — фильтруем по выбранной точке
    if (forceStore && selectedStoreIds.length > 0) {
      const selectedStoreId = selectedStoreIds[0];
      const stock = stockStores.find((s) => s.store?.id === selectedStoreId);
      if (!stock) return [];
      return [
        {
          name: stock.store?.name || storeMap.get(selectedStoreId) || "Магазин",
          available: stock.quantity - stock.reserved,
          total: stock.quantity,
        },
      ];
    }

    // Иначе — все точки
    return stockStores
      .map((s) => ({
        name: s.store?.name || storeMap.get(s.store?.id ?? 0) || "Магазин",
        available: s.quantity - s.reserved,
        total: s.quantity,
      }))
      .filter((s) => s.available >= 0);
  }, [stockStores, stockEnabled, forceStore, selectedStoreIds, storeMap]);

  // Стоп-листы — данные из itemStops (связь товара), названия из storeMap
  const availabilityByStores = useMemo(() => {
    if (stockEnabled) return [];
    if (itemStops.length === 0) return [];

    // Если принудительный выбор и точка выбрана — фильтруем по выбранной точке
    if (forceStore && selectedStoreIds.length > 0) {
      const selectedStoreId = selectedStoreIds[0];
      const stop = itemStops.find((s) => s.storeId === selectedStoreId);
      if (!stop) return [];
      return [
        {
          name: storeMap.get(selectedStoreId) || `Точка ${selectedStoreId}`,
          available: !stop.isStopped,
        },
      ];
    }

    // Иначе — все точки
    return itemStops.map((stop) => ({
      name: storeMap.get(stop.storeId) || `Точка ${stop.storeId}`,
      available: !stop.isStopped,
    }));
  }, [stockEnabled, itemStops, forceStore, selectedStoreIds, storeMap]);

  const totalAvailable = useMemo(() => {
    return availableStores.reduce((sum, s) => sum + s.available, 0);
  }, [availableStores]);

  const showNoStoreMessage =
    forceStore && !stockEnabled && selectedStoreIds.length === 0;
  const showUnavailableMessage =
    availabilityStatus === AvailabilityStatus.UNAVAILABLE;

  return {
    availableStores,
    availabilityByStores,
    totalAvailable,
    showNoStoreMessage,
    showUnavailableMessage,
  };
};
